// Marker motion and accuracy-circle geometry for the live map (pure).

/** [longitude, latitude] in degrees, as GeoJSON orders them. */
export type LngLat = [number, number];

export interface Motion {
  from: LngLat;
  to: LngLat;
  start: number;
  /** 0 jumps straight to `to`. */
  duration: number;
}

/** How long a marker glides to its new fix. */
export const GLIDE_MS = 1_200;
/** Moves longer than this jump instead of gliding (a member who was offline, or a bad fix). */
export const JUMP_DISTANCE_M = 20_000;
/** Accuracy circles are drawn no larger than this. */
export const MAX_ACCURACY_RADIUS_M = 20_000;

const EARTH_RADIUS_M = 6_371_008.8;

/**
 * A glide from where the marker is drawn now (`current`, null for a new
 * marker) to `target`.
 */
export function glide(current: LngLat | null, target: LngLat, now: number, reducedMotion: boolean): Motion {
  if (!current || reducedMotion || distanceM(current, target) > JUMP_DISTANCE_M) {
    return { from: target, to: target, start: now, duration: 0 };
  }
  // Cross the antimeridian the short way.
  const lngDelta = target[0] - current[0];
  const from: LngLat = [
    lngDelta > 180 ? current[0] + 360 : lngDelta < -180 ? current[0] - 360 : current[0],
    current[1],
  ];
  return { from, to: target, start: now, duration: GLIDE_MS };
}

/** Where the marker is drawn at time `t`. */
export function positionAt(motion: Motion, t: number): LngLat {
  if (motion.duration <= 0 || t >= motion.start + motion.duration) return motion.to;
  const p = easeOutCubic(Math.max(0, (t - motion.start) / motion.duration));
  const lng = motion.from[0] + (motion.to[0] - motion.from[0]) * p;
  const lat = motion.from[1] + (motion.to[1] - motion.from[1]) * p;
  return [wrapLng(lng), lat];
}

export function isMoving(motion: Motion, t: number): boolean {
  return motion.duration > 0 && t < motion.start + motion.duration;
}

function easeOutCubic(x: number): number {
  return 1 - (1 - x) ** 3;
}

function wrapLng(lng: number): number {
  if (lng >= -180 && lng <= 180) return lng;
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

/** Great-circle distance in metres. */
export function distanceM(a: LngLat, b: LngLat): number {
  const rad = Math.PI / 180;
  const dLat = (b[1] - a[1]) * rad;
  const dLng = (b[0] - a[0]) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[1] * rad) * Math.cos(b[1] * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * A closed ring approximating the circle of `radiusM` around `center`
 * (destination points along `steps` bearings), for a GeoJSON polygon.
 */
export function accuracyRing(
  center: LngLat,
  radiusM: number,
  steps = 64,
  maxRadiusM = MAX_ACCURACY_RADIUS_M,
): [number, number][] {
  const rad = Math.PI / 180;
  const angular = Math.min(Math.max(radiusM, 0), maxRadiusM) / EARTH_RADIUS_M;
  const lat1 = center[1] * rad;
  const lng1 = center[0] * rad;
  const ring: [number, number][] = [];
  for (let i = 0; i < steps; i++) {
    const bearing = (2 * Math.PI * i) / steps;
    const lat2 = Math.asin(Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearing));
    const lng2 =
      lng1 +
      Math.atan2(
        Math.sin(bearing) * Math.sin(angular) * Math.cos(lat1),
        Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2),
      );
    // Unwrapped, so a ring that crosses the antimeridian stays contiguous.
    ring.push([lng2 / rad, lat2 / rad]);
  }
  ring.push(ring[0]!);
  return ring;
}
